import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    { icon: 'ri-shield-check-line', title: 'Military-Grade Encryption', desc: 'Advanced AES-256 encryption protects your data from unauthorized access' },
    { icon: 'ri-eye-off-line', title: 'No-Log Policy', desc: 'We never track, collect, or share your personal browsing data' },
    { icon: 'ri-delete-bin-line', title: 'Smart Junk Detection', desc: 'Intelligent algorithms identify and remove unnecessary files automatically' },
    { icon: 'ri-battery-charge-line', title: 'Battery Optimization', desc: 'Efficient resource management extends your device battery life' },
    { icon: 'ri-global-line', title: 'Global Server Network', desc: 'Connect to high-speed servers in over 50 countries worldwide' },
    { icon: 'ri-hand-coin-line', title: 'One-Tap Cleaning', desc: 'Simple interface makes device optimization effortless and quick' },
    { icon: 'ri-shield-flash-line', title: 'Real-Time Protection', desc: 'Continuous monitoring ensures your device stays secure 24/7' },
    { icon: 'ri-dashboard-line', title: 'Privacy Dashboard', desc: 'Comprehensive overview of your security status and device health' }
  ];

  const navItems = ['home', 'about', 'products', 'features', 'contact'];
  const footerNavItems = ['Home', 'About', 'Products', 'Features', 'Contact'];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-[1440px] mx-auto px-20 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/db80ebc0-343b-474a-9339-ef3d019cd163.png" alt="DIMEL OOD Logo" className="h-10 w-10 object-contain" />
              <span className={`text-2xl font-semibold tracking-wide ${scrolled ? 'text-gray-900' : 'text-white'}`}>DIMEL OOD</span>
            </div>
            <div className="flex items-center gap-12">
              {navItems.map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className={`text-base font-medium capitalize relative group cursor-pointer transition-colors ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}
                >
                  {item}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-teal-500 transition-all duration-300 group-hover:w-full"></span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/8358afc99143a0a6a50bbc60a52f0fcd.jpg" 
            alt="Hero Background" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/60 to-gray-900/70"></div>
        </div>
        
        <div className="relative max-w-[1440px] mx-auto px-20 py-32 w-full">
          <div className="grid grid-cols-5 gap-16 items-center">
            <div className="col-span-3">
              <h1 className="text-7xl leading-tight mb-8">
                <span className="block font-light text-white">Crafting Premium</span>
                <span className="block font-bold text-white">iOS Applications</span>
                <span className="block font-light italic text-white">for Modern Security</span>
              </h1>
              <p className="text-xl text-gray-200 leading-relaxed max-w-2xl">
                DIMEL OOD is a team of experts developing modern applications for the iOS platform. Our products are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>
            <div className="col-span-2 relative h-[600px]">
              <div className="absolute top-0 right-0 w-64 h-96 transform rotate-12">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iPhone%20mockup%20displaying%20VPN%20security%20app%20interface%20with%20glowing%20blue%20shield%20icon%20and%20connection%20status%2C%20floating%20on%20gradient%20background%20with%20soft%20shadows%2C%20professional%20product%20photography%20style%2C%20clean%20and%20minimal%20design&width=400&height=800&seq=phone-mock-001&orientation=portrait" 
                  alt="VPN App" 
                  className="w-full h-full object-contain drop-shadow-2xl"
                />
              </div>
              <div className="absolute top-32 right-48 w-64 h-96 transform -rotate-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iPhone%20mockup%20showing%20cleaner%20app%20interface%20with%20storage%20optimization%20graphics%20and%20teal%20accent%20colors%2C%20floating%20with%20realistic%20shadows%2C%20professional%20product%20photography%2C%20minimalist%20design%20with%20clean%20background&width=400&height=800&seq=phone-mock-002&orientation=portrait" 
                  alt="Cleaner App" 
                  className="w-full h-full object-contain drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-20">
          <div className="text-center mb-20">
            <span className="inline-block px-6 py-2 bg-teal-500 text-white text-xs uppercase tracking-widest rounded-full mb-8">Who We Are</span>
            <h2 className="text-6xl font-bold text-gray-900">Expert Team Behind Innovation</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-12 items-center mt-20">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20software%20development%20team%20working%20together%20in%20modern%20office%20environment%2C%20diverse%20group%20of%20iOS%20developers%20collaborating%20on%20mobile%20applications%2C%20bright%20and%20clean%20workspace%20with%20computers%20and%20technology%2C%20professional%20corporate%20photography%20style&width=600&height=700&seq=team-photo-001&orientation=portrait" 
                alt="DIMEL OOD Team" 
                className="w-full h-[500px] object-cover rounded-3xl shadow-lg"
              />
            </div>
            
            <div>
              <p className="text-lg text-gray-700 leading-relaxed">
                At DIMEL OOD, we combine technical expertise with creative innovation to deliver exceptional iOS applications. Our team specializes in developing security-focused solutions that protect user privacy while maintaining seamless functionality. We are committed to excellence in every line of code we write.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-white">
        <div className="max-w-[1440px] mx-auto px-20">
          <div className="mb-24">
            <span className="inline-block px-6 py-2 bg-teal-500 text-white text-xs uppercase tracking-widest rounded-full mb-6">Our Solutions</span>
            <h2 className="text-6xl font-bold text-gray-900">Security-First Applications</h2>
          </div>
          
          <div className="grid grid-cols-2 gap-10">
            <div className="group relative h-[600px] rounded-[32px] overflow-hidden cursor-pointer transition-all duration-300 hover:transform hover:-translate-y-2 hover:shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-400"></div>
              <div className="relative h-full p-12 flex flex-col">
                <div className="flex-1 flex items-center justify-center">
                  <img 
                    src="https://readdy.ai/api/search-image?query=isometric%20iPhone%20illustration%20showing%20VPN%20application%20interface%20with%20glowing%20shield%20icon%2C%20secure%20connection%20visualization%20with%20flowing%20data%20particles%20and%20network%20lines%2C%20blue%20and%20teal%20color%20scheme%2C%20modern%203D%20render%20style%20with%20dramatic%20lighting&width=500&height=600&seq=vpn-product-001&orientation=portrait" 
                    alt="VPN App Interface" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="mt-8">
                  <h3 className="text-4xl font-bold text-white mb-4">VPN</h3>
                  <p className="text-base text-white/90 leading-relaxed">
                    Secure your online presence with military-grade encryption. Our VPN provides fast, reliable connections to servers worldwide while maintaining your complete privacy and anonymity.
                  </p>
                </div>
              </div>
            </div>

            <div className="group relative h-[600px] rounded-[32px] overflow-hidden cursor-pointer transition-all duration-300 hover:transform hover:-translate-y-2 hover:shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-teal-600"></div>
              <div className="relative h-full p-12 flex flex-col">
                <div className="flex-1 flex items-center justify-center">
                  <img 
                    src="https://readdy.ai/api/search-image?query=isometric%20iPhone%20illustration%20displaying%20cleaner%20app%20interface%20with%20storage%20optimization%20graphics%2C%20animated%20cleaning%20process%20with%20particle%20effects%20and%20progress%20indicators%2C%20teal%20and%20dark%20gray%20color%20palette%2C%20modern%203D%20render%20with%20glowing%20UI%20elements&width=500&height=600&seq=cleaner-product-001&orientation=portrait" 
                    alt="Cleaner App Interface" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="mt-8">
                  <h3 className="text-4xl font-bold text-white mb-4">Cleaner</h3>
                  <p className="text-base text-white/90 leading-relaxed">
                    Optimize your device performance with intelligent cleaning technology. Remove junk files, manage storage efficiently, and keep your iPhone running at peak performance with one tap.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-gray-50">
        <div className="max-w-[1440px] mx-auto px-20">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900">Comprehensive Protection Features</h2>
          </div>
          
          <div className="grid grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 transition-all duration-300 hover:shadow-lg hover:transform hover:-translate-y-1">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-500/10 rounded-full mb-6">
                  <i className={`${feature.icon} text-4xl text-blue-500`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, white 2px, transparent 2px)', backgroundSize: '40px 40px' }}></div>
        </div>
        
        <div className="relative max-w-[1440px] mx-auto px-20">
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-4">
              <img src="https://public.readdy.ai/ai/img_res/db80ebc0-343b-474a-9339-ef3d019cd163.png" alt="DIMEL OOD Logo" className="h-10 w-10 object-contain" />
              <h3 className="text-3xl font-semibold text-white">DIMEL OOD</h3>
            </div>
            <p className="text-base text-gray-400">Crafting premium iOS applications for modern security</p>
          </div>
          
          <div className="grid grid-cols-4 gap-16 mb-16">
            <div>
              <h4 className="text-sm uppercase tracking-widest text-white mb-6">Location</h4>
              <div className="space-y-2 text-base text-gray-400">
                <p className="flex items-start gap-2">
                  <i className="ri-map-pin-line text-teal-500 mt-1"></i>
                  <span>1 Rayko Daskalov str.</span>
                </p>
                <p className="flex items-start gap-2">
                  <i className="ri-building-line text-teal-500 mt-1"></i>
                  <span>City Pernik</span>
                </p>
                <p className="flex items-start gap-2">
                  <i className="ri-mail-line text-teal-500 mt-1"></i>
                  <span>Zip 2300</span>
                </p>
                <p className="flex items-start gap-2">
                  <i className="ri-flag-line text-teal-500 mt-1"></i>
                  <span>Country Bulgaria</span>
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm uppercase tracking-widest text-white mb-6">Get in Touch</h4>
              <div className="space-y-4">
                <div>
                  <p className="text-teal-500 text-sm mb-1 flex items-center gap-2">
                    <i className="ri-mail-line"></i>
                    <span>Email</span>
                  </p>
                  <a href="mailto:info@dimelood.com" className="text-base text-gray-400 hover:text-white transition-colors cursor-pointer">info@dimelood.com</a>
                </div>
                <div>
                  <p className="text-teal-500 text-sm mb-1 flex items-center gap-2">
                    <i className="ri-phone-line"></i>
                    <span>Phone</span>
                  </p>
                  <a href="tel:+13345094602" className="text-base text-gray-400 hover:text-white transition-colors cursor-pointer">+1 3345094602</a>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm uppercase tracking-widest text-white mb-6">Navigation</h4>
              <div className="space-y-3">
                {footerNavItems.map((item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item.toLowerCase())}
                    className="block text-base text-gray-400 hover:text-white transition-colors cursor-pointer"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="text-sm uppercase tracking-widest text-white mb-6">Stay Updated</h4>
              <p className="text-sm text-gray-400 mb-4">Subscribe to our newsletter for the latest updates and security tips.</p>
              <div className="flex items-center gap-2">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  className="flex-1 px-4 py-3 bg-gray-800 text-white text-sm rounded-lg border border-gray-700 focus:outline-none focus:border-teal-500 transition-colors"
                />
                <button className="w-12 h-12 flex items-center justify-center bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
              </div>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-800">
            <p className="text-center text-sm text-gray-500">© 2025 DIMEL OOD. All rights reserved.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
